import * as React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, parseJSON } from "date-fns";
import { Box, Button, FormField, Grid, Markdown, ResponsiveContext, Stack, Text, TextArea, TextInput } from "grommet";
import { DateTimePicker, FormatDateTime } from "../components/DateTimePicker";
import StyledModal from "../components/StyledModel";
import OrganizationSearch from "../organizations/OrganizationSearch";
import { Organization } from "../organizations/OrganizationStore";
import { CreateInvite, CreateOrganizationInviteRequest, OrganizationEntryViewModel, OrganizationInviteViewModel } from "../registration/RegistrationStore";
import { addParticipant, TournamentViewModel, updateParticipant } from "./TournamentsStore";
import Avatar from "react-avatar";
import OrganizationBlock from "../organizations/OrganizationBlock";
import TournamentBlock from "./TournamentBlock";
import { size } from "lodash";
import { AliasTile } from "../aliases/AliasTile";
import { TeamRow } from "../teams/TeamsManagement";
import LinkButton from "../components/LinkButton";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;

export const TournamentInvites = ({ tournament, onChange }: { tournament: TournamentViewModel; onChange: Function }) => {
    const [showAddInviteModal, setShowAddInviteModal] = React.useState(false);
    const [organization, setOrganization] = React.useState({} as Organization);

    const [name, setName] = React.useState("");
    const [content, setContent] = React.useState("");
    const [startDate, setStartDate] = React.useState(new Date().toISOString());
    const [endDate, setEndDate] = React.useState(new Date().toISOString());
    const [showDateEdit, setShowDateEdit] = React.useState(false);

    const createInvite = async () => {
        if (!organization.id) {
            setCreateError("Organization is required");
        } else {
            let res = await CreateInvite({
                content,
                endDate,
                name,
                organizationID: organization.id,
                startDate,
                tournamentID: tournament.id,
            } as CreateOrganizationInviteRequest);

            if (res.success) {
                setShowAddInviteModal(false);
                onChange();
            } else {
                setCreateError(res.errors.join("\n"));
            }
        }
    };

    const [createError, setCreateError] = React.useState("");

    const acceptEntry = async (entry: OrganizationEntryViewModel) => {
        if (tournament.id) {
            let res = await addParticipant({
                tournamentID: tournament.id,
                teamID: entry.teamID,
                aliases: entry.participatingAliases.map((a) => a.aliasID),
            });

            if (res.success) {
                onChange();
            } else {
                setCreateError(res.errors.join("\n"));
            }
        }
    };

    const updateEntry = async (entry: OrganizationEntryViewModel) => {
        let tournamentTeam = tournament.participants?.filter((p) => p.teamID == entry.teamID)[0];

        if (tournamentTeam) {
            let res = await updateParticipant({
                participantID: tournamentTeam.id,
                aliasIDs: entry.participatingAliases.map((a) => a.aliasID),
                seed: tournamentTeam.seed,
            });

            if (res.success) {
                onChange();
            } else {
                setCreateError(res.errors.join("\n"));
            }
        }
    };

    const copyFromFirstInvite = () => {
        if (tournament.organizationInvites.length > 0) {
            let firstInvite = tournament.organizationInvites[0];

            setName(firstInvite.name);
            setContent(firstInvite.content);
            setStartDate(firstInvite.startDate);
            setEndDate(firstInvite.endDate);
        }
    };

    return (
        <Box pad={"small"}>
            {tournament.organizationInvites.map((o) => (
                <OrganizationInvite tournament={tournament} invite={o} key={o.id} accept={acceptEntry} update={updateEntry}></OrganizationInvite>
            ))}
            {!tournament.locked && <Button icon={plus} label="Add Invite" onClick={() => setShowAddInviteModal(true)}></Button>}

            <Text>
                <pre>{createError}</pre>
            </Text>

            <StyledModal
                isOpen={showAddInviteModal}
                setIsOpen={(open) => {
                    setShowAddInviteModal(open);
                }}
            >
                <Box width={"medium"}>
                    <Text margin={{ left: "small" }}>Choose Organization</Text>
                    <OrganizationSearch onSelect={(organization) => setOrganization(organization)}></OrganizationSearch>

                    {tournament.organizationInvites.length > 0 && (
                        <Box direction="row" fill>
                            <Button label="Copy from First Invite" primary onClick={() => copyFromFirstInvite()}></Button>
                        </Box>
                    )}

                    <FormField label="Name">
                        <TextInput size="xs" value={name} onChange={(e) => setName(e.target.value)} />
                    </FormField>
                    <FormField label="Content">
                        <TextArea size="xs" value={content} onChange={(e) => setContent(e.target.value)} />
                    </FormField>
                    <Box border="bottom" margin={{ bottom: "small" }}>
                        <Text margin={{ left: "small" }}>Invitations Open</Text>
                        <Text margin={{ left: "small", top: "small" }}>{FormatDateTime(parseJSON(startDate), "yyyy/MM/dd")}</Text>
                    </Box>
                    <Box margin={{ bottom: "small" }}>
                        <Text margin={{ left: "small" }}>Invitations Close</Text>
                        <Box direction="row" justify="between" border="bottom">
                            <Text margin={{ left: "small", top: "small" }}>{FormatDateTime(parseJSON(endDate), "yyyy/MM/dd")}</Text>
                            <Box hoverIndicator="background-front" onClick={() => setShowDateEdit(true)} pad="small">
                                <FontAwesomeIcon icon={["fad", "edit"]}></FontAwesomeIcon>
                            </Box>
                        </Box>
                    </Box>
                    <Box direction="row" justify="end" fill>
                        <Button label="Create" primary onClick={() => createInvite()}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showDateEdit} setIsOpen={setShowDateEdit}>
                <Box gap="small" align="center">
                    <Box>
                        <Text margin={{ left: "small" }}>Invitations Open</Text>
                        <DateTimePicker value={startDate} onChange={setStartDate}></DateTimePicker>
                    </Box>
                    <Box>
                        <Text margin={{ left: "small" }}>Invitations Close</Text>
                        <DateTimePicker value={endDate} onChange={setEndDate}></DateTimePicker>
                    </Box>
                    <Box direction="row" justify="end" fill>
                        <Button label="Confirm" onClick={() => setShowDateEdit(false)}></Button>
                    </Box>
                </Box>
            </StyledModal>
        </Box>
    );
};

export const OrganizationInvite = ({ tournament, invite, accept, update, children }: { tournament: TournamentViewModel; invite: OrganizationInviteViewModel; accept: Function; update: Function; children?: React.ReactNode }) => {
    const size = React.useContext(ResponsiveContext);

    const tournamentContainsTeam = (teamID) => tournament.participants?.some((p) => p.teamID == teamID);

    const entryHasChanges = (entry: OrganizationEntryViewModel) => {
        //tournament has team
        if (tournamentContainsTeam(entry.teamID)) {
            let tournamentTeam = tournament.participants?.filter((p) => p.teamID == entry.teamID)[0];

            //tournament team has same aliases
            if (tournamentTeam) {
                return !entry.participatingAliases.every((a) => tournamentTeam.aliases.some((pa) => pa.aliasID == a.aliasID)) || !tournamentTeam.aliases.every((a) => entry.participatingAliases.some((pa) => pa.aliasID == a.aliasID));
            }
        } else {
            return true;
        }

        return false;
    };

    const doAccept = (entry: OrganizationEntryViewModel) => {
        if (tournamentContainsTeam(entry.teamID)) {
            update(entry);
        } else {
            accept(entry);
        }
    };

    return (
        <Stack anchor="top-right">
            <Box background={"background"} hoverIndicator="background-back" gap="small" pad="small">
                <Text>{invite.name}</Text>
                <Grid columns={["150px", "auto"]}>
                    <OrganizationBlock id={invite.organizationID}></OrganizationBlock>
                    <Box>
                        <Markdown>{invite.content}</Markdown>
                        <Text size="small">{invite.organizationEntries?.length || 0} entries</Text>
                        <Box gap="small">
                            {invite.organizationEntries.map((entry) => (
                                <Box border={{ color: "text" }}>
                                    <TeamRow team={entry.team} key={entry.id}>
                                        <Box direction={size == "small" ? "column" : "row"} gap="small">
                                            {entry.participatingAliases.map((aliasParticipant) => (
                                                <AliasTile key={aliasParticipant.id} alias={aliasParticipant.alias}></AliasTile>
                                            ))}
                                            <Box justify="end">
                                                {entryHasChanges(entry) && (
                                                    <Button
                                                        label={tournamentContainsTeam(entry.teamID) ? "Update" : "Accept"}
                                                        onClick={() => {
                                                            doAccept(entry);
                                                        }}
                                                    ></Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </TeamRow>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Box>
            <Box pad={"xsmall"}>
                <LinkButton icon={edit} label="Edit" path={`/registration/invites/${invite.id}/edit`}></LinkButton>
            </Box>
        </Stack>
    );
};

export const TournamentInvite = ({ invite, children }: { invite: OrganizationInviteViewModel; children?: React.ReactNode }) => {
    return (
        <Box background={"background"} hoverIndicator="background-back" gap="small" pad="small">
            <Text>{invite.name}</Text>
            <Grid columns={["150px", "auto", "110px"]}>
                <TournamentBlock tournament={invite.tournament}></TournamentBlock>
                <Box>
                    <Markdown>{invite.content}</Markdown>
                    <Text size="small">{invite.organizationEntries?.length || 0} entries</Text>
                    <Text size="small">
                        Entries Close{" "}
                        <Text size="small" color={"brand"}>
                            {format(parseJSON(invite.endDate), "yyyy/MM/dd")}
                        </Text>
                    </Text>
                </Box>
                <Box>{children}</Box>
            </Grid>
        </Box>
    );
};
